// ============================================================================
// OMS Escalations — direct Kylas calls (no Django hop), ported from the CRM's
// escalation tab (materialdepot-crm/components/sales-dashboard/escalation).
//
// Uses `fetch`, NOT the app's axios: axios carries the global
// `Authorization: JWT <token>` header (src/index.tsx), which must never be sent
// to a third party. Kylas allows browser calls (CORS `*`, `Api-Key` header).
//
// REACT_APP_KYLAS_API_KEY is inlined into the bundle by CRA, so anyone who can
// load the OMS can read it. Accepted trade-off for "no backend" — rotate the
// key if this app is ever opened beyond internal staff.
// ============================================================================

const KYLAS_BASE = process.env.REACT_APP_KYLAS_API_BASE_URL || "https://api.kylas.io/v1";
const KYLAS_KEY = process.env.REACT_APP_KYLAS_API_KEY || "";

export const kylasConfigured = () => !!KYLAS_KEY;

// Escalation pipeline → "Internal Dependency " stage (Kylas keeps a trailing
// space in the name, so match on id). Dependency = "Passed to Procurement".
export const ESCALATION_PIPELINE_ID = 32620;
export const INTERNAL_DEPENDENCY_STAGE_ID = 227597;
export const DEPENDENCY_FIELD = "cfDependency";
export const PASSED_TO_PROCUREMENT_ID = 184538;

export const ESCALATION_PIPELINE_NAME = "Escalation pipeline";
export const IN_PROGRESS_STAGE_ID = 227596;
export const IN_PROGRESS_STAGE_NAME = "In Progress";

export const kylasDealUrl = (id: number) => `https://app.kylas.io/sales/deals/details/${id}`;

export type NamedRef = { id: number; name?: string };

export interface KylasDeal {
  id: number;
  name: string;
  ownedBy?: NamedRef;
  pipeline?: NamedRef;
  pipelineStage?: NamedRef;
  createdAt?: string;
  updatedAt?: string;
  customFieldValues?: Record<string, unknown>;
  associatedContacts?: NamedRef[];
}

export interface KylasNote {
  id: number;
  description: string;
  createdAt?: string;
  createdBy?: string;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Kylas rate-limits hard; retry a 429 with backoff, same as the CRM's pacedFetch.
async function kylasFetch(path: string, init: RequestInit = {}): Promise<any> {
  if (!KYLAS_KEY) throw new Error("Kylas API key missing (REACT_APP_KYLAS_API_KEY)");
  const headers: Record<string, string> = { "api-key": KYLAS_KEY, Accept: "application/json" };
  // Multipart bodies must set their own boundary, so leave Content-Type off for FormData.
  if (init.body && !(init.body instanceof FormData)) headers["Content-Type"] = "application/json";
  for (let attempt = 0; ; attempt++) {
    const res = await fetch(`${KYLAS_BASE}${path}`, { ...init, headers: { ...headers, ...(init.headers as any) } });
    if (res.status === 429 && attempt < 3) {
      await sleep(800 * (attempt + 1));
      continue;
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.message || body?.error || `Kylas request failed: ${res.status}`);
    }
    return res.status === 204 ? null : res.json().catch(() => null);
  }
}

const SEARCH_FIELDS = [
  "name", "ownedBy", "pipeline", "pipelineStage", "id",
  "createdAt", "updatedAt", "customFieldValues", "associatedContacts",
];

export const isPassedToProcurement = (deal: KylasDeal) => {
  const v = deal.customFieldValues?.[DEPENDENCY_FIELD];
  const opts = Array.isArray(v) ? v : v ? [v] : [];
  return opts.some((o: any) => o?.id === PASSED_TO_PROCUREMENT_ID);
};

// Kylas search cannot filter on the cfDependency multi-picklist (every rule
// shape returns 0), so pull the whole Internal Dependency stage — a few dozen
// deals — and filter "Passed to Procurement" here.
export async function listProcurementEscalations(): Promise<KylasDeal[]> {
  const rules = [
    { id: "pipeline", field: "pipeline", type: "string", input: "select", operator: "in", value: [ESCALATION_PIPELINE_ID] },
    { id: "pipelineStage", field: "pipelineStage", type: "string", input: "select", operator: "in", value: [INTERNAL_DEPENDENCY_STAGE_ID] },
  ];
  const all: KylasDeal[] = [];
  for (let page = 0; page < 10; page++) {
    const data = await kylasFetch(`/search/deal?page=${page}&size=100&sort=${encodeURIComponent("updatedAt,desc")}`, {
      method: "POST",
      body: JSON.stringify({ fields: SEARCH_FIELDS, jsonRule: { condition: "AND", rules, valid: true } }),
    });
    all.push(...(data?.content ?? []));
    if (page + 1 >= (data?.totalPages ?? 0)) break;
  }
  return all.filter(isPassedToProcurement);
}

// GET /deals/{id} nests the stage under `pipeline.stage` and returns
// `pipelineStage: null` (search returns the opposite), so normalise to one shape.
export async function getDeal(dealId: number): Promise<KylasDeal> {
  const d = await kylasFetch(`/deals/${dealId}`);
  return { ...d, pipelineStage: d?.pipelineStage ?? d?.pipeline?.stage };
}

const stripHtml = (s: string) => s.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

export async function listDealNotes(dealId: number, ownerId?: number): Promise<KylasNote[]> {
  const qs = new URLSearchParams({
    targetEntityId: String(dealId),
    targetEntityType: "DEAL",
    sort: "createdAt,desc",
    page: "0",
    size: "50",
  });
  if (ownerId) qs.set("targetEntityOwnerId", String(ownerId));
  const data = await kylasFetch(`/notes/relation?${qs}`);
  return (data?.content ?? []).map((n: any) => ({
    id: n.id,
    description: stripHtml(n.description ?? ""),
    createdAt: typeof n.createdAt === "number" ? new Date(n.createdAt).toISOString() : n.createdAt,
    createdBy: n.createdBy?.name,
  }));
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");

// Same note shape the CRM writes: "[Author] text", so the escalation team can
// tell procurement's notes apart (the API key posts as one shared Kylas user).
export function addDealNote(dealId: number, author: string, text: string) {
  return kylasFetch(`/notes/relation`, {
    method: "POST",
    body: JSON.stringify({
      sourceEntity: { description: `<div><b>[${escapeHtml(author)}]</b> ${escapeHtml(text)}</div>` },
      targetEntityId: String(dealId),
      targetEntityType: "DEAL",
    }),
  });
}

export interface ProcurementItem {
  sku: string;
  quantity: number | null;
}

export interface ProcurementUpdate {
  items: ProcurementItem[];
  leadTimeDays: number | null;
  remarks: string;
}

const itemFilled = (i: ProcurementItem) => !!i.sku?.trim() && i.quantity != null && i.quantity > 0;

// "Complete" = every item row has SKU + quantity, and lead time + remarks are set.
// Only a complete update hands the ticket back to the escalation team.
export const isUpdateComplete = (u: ProcurementUpdate) =>
  u.items.length > 0 &&
  u.items.every(itemFilled) &&
  u.leadTimeDays != null &&
  u.leadTimeDays >= 0 &&
  !!u.remarks.trim();

function procurementUpdateHtml(author: string, u: ProcurementUpdate) {
  const items = u.items.filter((i) => i.sku?.trim() || i.quantity != null);
  const itemRows = items.length
    ? items.map((i) => `• ${escapeHtml(i.sku?.trim() || "—")} — Qty ${i.quantity ?? "—"}`).join("<br/>")
    : "—";
  return (
    `<div><b>[${escapeHtml(author)}]</b> Procurement update</div>` +
    `<div><b>Item SKU &amp; Quantity:</b><br/>${itemRows}</div>` +
    `<div><b>Updated lead time:</b> ${u.leadTimeDays != null ? `${u.leadTimeDays} day${u.leadTimeDays === 1 ? "" : "s"}` : "—"}</div>` +
    `<div><b>Remarks:</b> ${u.remarks.trim() ? escapeHtml(u.remarks.trim()) : "—"}</div>`
  );
}

// Same JSON-Patch Django's note handler sends (md/integration/kylas/services/inbound/note_handler.py).
export function moveDealToInProgress(dealId: number) {
  return kylasFetch(`/deals/${dealId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json-patch+json" },
    body: JSON.stringify([
      {
        op: "replace",
        path: "/pipeline",
        value: {
          id: ESCALATION_PIPELINE_ID,
          name: ESCALATION_PIPELINE_NAME,
          stage: { id: IN_PROGRESS_STAGE_ID, name: IN_PROGRESS_STAGE_NAME },
        },
      },
    ]),
  });
}

// Note first, stage second. Django's note webhook holds Internal Dependency
// tickets where they are; posting the note while the deal is still there keeps
// that webhook from also routing it (e.g. to Needs Update) after our move.
// Returns whether the stage was moved; throws if the note itself failed.
export async function submitProcurementUpdate(
  dealId: number,
  author: string,
  u: ProcurementUpdate
): Promise<{ moved: boolean; moveError?: string }> {
  await kylasFetch(`/notes/relation`, {
    method: "POST",
    body: JSON.stringify({
      sourceEntity: { description: procurementUpdateHtml(author, u) },
      targetEntityId: String(dealId),
      targetEntityType: "DEAL",
    }),
  });
  if (!isUpdateComplete(u)) return { moved: false };
  // Re-read so a ticket someone already moved on in Kylas is not dragged back.
  const fresh = await getDeal(dealId).catch(() => null);
  if (fresh && fresh.pipelineStage?.id !== INTERNAL_DEPENDENCY_STAGE_ID) {
    return { moved: false, moveError: `Ticket is now at "${fresh.pipelineStage?.name?.trim()}", stage left as is` };
  }
  try {
    await moveDealToInProgress(dealId);
    return { moved: true };
  } catch (e: any) {
    return { moved: false, moveError: e?.message || "Could not move stage" };
  }
}

// Kylas has no working endpoint to list a deal's documents, so — like the CRM —
// each upload is followed by an "uploaded: <names>" note that shows in Notes.
export async function uploadDealFiles(dealId: number, author: string, files: File[]) {
  const form = new FormData();
  files.forEach((f) => form.append("files[]", f));
  form.append("entityId", String(dealId));
  form.append("entityType", "deal");
  await kylasFetch(`/documents`, { method: "POST", body: form });
  await addDealNote(dealId, author, `uploaded: ${files.map((f) => f.name).join(", ")}`).catch(() => undefined);
}

export interface TimelineEntry {
  id: number;
  event: string;
  description: string;
  performedBy: string;
  createdAt: string;
}

const fieldValue = (v: unknown): string => {
  if (v == null) return "";
  if (Array.isArray(v)) return v.map((x: any) => x?.name ?? "").filter(Boolean).join(", ");
  if (typeof v === "object" && "name" in (v as any)) return (v as any).name;
  return String(v);
};

const TRACKED_FIELDS: Record<string, string> = {
  cfResolution: "Resolution",
  cfRefundCnAmount: "Refund/CN Value",
  cfEscalationClassification: "Attribution",
  cfDependency: "Dependency",
};

export async function listDealTimeline(dealId: number): Promise<TimelineEntry[]> {
  const data = await kylasFetch(`/feeds/search?page=0&size=50&sort=${encodeURIComponent("performedAt,desc")}`, {
    method: "POST",
    body: JSON.stringify({
      jsonRule: {
        condition: "AND",
        rules: [
          { field: "related_to", operator: "equal", id: "related_to", type: "related_lookup", value: { entity: "deal", id: String(dealId) } },
          { field: "systemDefault", operator: "equal", id: "systemDefault", type: "boolean", value: false },
          { field: "category", operator: "equal", id: "category", type: "string", value: "ALL" },
        ],
        valid: true,
      },
    }),
  });
  const out: TimelineEntry[] = [];
  for (const f of data?.content ?? []) {
    const ev = f.action?.event ?? "";
    const base = { id: f.id, description: "", performedBy: f.performedBy?.name ?? "System", createdAt: f.createdAt ?? "" };
    const next = f.payload?.new ?? {};
    if (ev === "CREATED") out.push({ ...base, event: "Created" });
    else if (ev === "PIPELINE_CHANGED") out.push({ ...base, event: `→ ${next.pipelineStage?.name ?? "Unknown"}` });
    else if (ev === "DEAL_CLOSED") out.push({ ...base, event: "Closed" });
    else if (ev === "ASSOCIATED_ENTITY_CREATED" && f.action?.associatedEntity?.entity === "NOTE") {
      const s = stripHtml(next.description ?? "");
      out.push({ ...base, event: "Note", description: s.length > 60 ? `${s.slice(0, 60)}…` : s });
    } else if (ev === "UPDATED") {
      const changes = Object.keys(next)
        .filter((k) => k in TRACKED_FIELDS || /csat|score/i.test(k))
        .map((k) => `${TRACKED_FIELDS[k] ?? k.replace(/^cf/, "")}: ${fieldValue(next[k])}`)
        .filter((s) => !s.endsWith(": "));
      if (changes.length) out.push({ ...base, event: changes.join(" · ") });
    }
  }
  return out;
}

export const cfDisplay = (v: unknown): string => fieldValue(v) || "—";
